import { Markdown } from "@hub/ui/markdown";
import { useEffect, useState } from "react";

interface ReleaseNote {
  tag: string;
  publishedAt: string | null;
  prerelease: boolean;
  body: string;
}

/**
 * Formats a calendar date (YYYY-MM-DD) as a localized date string without timezone conversion.
 * A release date is a calendar date, not an instant in time, so we format the literal
 * date value (Sep 11, not Sep 10 for America/Chicago viewers).
 */
export function published(at: string | null): string {
  if (!at) return "";

  // Parse the YYYY-MM-DD string into year, month, day components
  const [yearStr, monthStr, dayStr] = at.split("-");
  const year = Number.parseInt(yearStr ?? "", 10);
  const month = Number.parseInt(monthStr ?? "", 10);
  const day = Number.parseInt(dayStr ?? "", 10);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return "";

  // Create a UTC date at that calendar date
  const date = new Date(Date.UTC(year, month - 1, day));

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** Every release, newest first, with its notes. */
export function ReleasesPage() {
  const [releases, setReleases] = useState<ReleaseNote[] | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  useEffect(() => {
    // Plain fetch, not apiFetch: apiFetch turns a 401 into a redirect to
    // /login, which on this public page would bounce every anonymous visitor
    // to a login screen they have no reason to see. A non-200 response here
    // just means the page has no data — treat it as a state to show, not a
    // signal to navigate away.
    fetch("/api/releases/notes", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((r: { releases: ReleaseNote[]; unavailable?: string } | null) => {
        if (r) {
          setReleases(r.releases);
          setUnavailable(r.unavailable ?? null);
        } else {
          setReleases([]);
          setUnavailable("Failed to load releases");
        }
      })
      .catch((e: Error) => {
        setReleases([]);
        setUnavailable(e.message);
      });
  }, []);

  return (
    <div className="w-full">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
        Releases
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        What shipped in each version of Nymbl Helm.
      </p>

      {unavailable ? (
        <p className="mt-6 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          {unavailable}
        </p>
      ) : null}

      {releases === null ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
      ) : releases.length === 0 && !unavailable ? (
        <p className="mt-6 text-sm text-muted-foreground">No releases yet.</p>
      ) : null}

      <div className="mt-8 flex flex-col gap-10">
        {(releases ?? []).map((r) => (
          <section key={r.tag} id={r.tag} className="scroll-mt-20">
            <div className="flex flex-wrap items-baseline gap-3 border-b border-border pb-2">
              <h2 className="font-display text-lg font-semibold tracking-tight text-foreground">
                {r.tag}
              </h2>
              {r.prerelease ? (
                <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                  prerelease
                </span>
              ) : null}
              <span className="ml-auto text-xs text-muted-foreground">
                {published(r.publishedAt)}
              </span>
            </div>

            {r.body ? (
              <div className="mt-4 text-sm leading-relaxed text-foreground">
                <Markdown source={r.body} />
              </div>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">No notes for this release.</p>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
